import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  WithFetch,
  FetchData,
  WithTableLayout,
  TABLE_ACTION,
  TABLE_ACTION_STATE
} from '@qrios/frontend-library';

import { Dialog } from '../../../../../common/dialog';
import endpoints from '../../../../../../api/endpoints';
import TableFooterInfo from '../../../../../common/table/footerInfo';
import { formatToAmount } from '../../../../../../constants/helpers/utils';
import GlobalLoaderOverlay from '../../../../../common/with-global-overlay';
import SearchByOperationId from '../../../../../common/search-by-operation-id';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { getQueryParams, handleRefreshOptions } from '../../../../../common/billing/apiUtils';
import { useSearchByOperationId } from '../../../../../common/search-by-operation-id/useSearchByOperationId';

import locales from './locales';
import { columns } from './columns';
import FilterForm from './filterForm';
import OperationDetails from './operation-details';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { activeFiltersCountMapper } from './filters';
import { useShowOperationDetails } from './operation-details/useShowOperationDetails';
import { PayoutBillingFilters, PayoutBillingResponse, PayoutBillingTableRowActions } from './interfaces';

import { handleEndDateFilter } from '../../../../../../constants/helpers/dates';
import { billingTableEmptyMsg } from '../../../../../../constants/utils';
import { FILTERS_PATH, FiltersContext } from '../../../../../common/filters/filtersContext';
import { PayoutReportCreateForm } from '../reports/interfaces';
import { PORTAL_PATH } from '../../../../../routes/paths';

const apiOptions = {
  totalCost: true
};

const Billing = () => {
  const history = useHistory();
  const { filtersValues, handleUpdateFilters } = useContext(FiltersContext);
  const [activeFilters, setActiveFilters] = useState(filtersValues.payout.billing);
  const initFetchOptions = getQueryParams({ filters: activeFilters, apiOptions });

  const {
    openDetailsDialog,
    handleOpenDetailsDialog,
    handleCloseDetailsDialog,
    selectedOperationDetails,
    waitingForDialogOpening
  } = useShowOperationDetails();

  const {
    searchByOperationIdState,
    handleSearchByOperationId,
    isSearching,
    openSearchOperationDialog,
    handleSearchOperationOpenDialog,
    handleSearchOperationCloseDialog
  } = useSearchByOperationId({
    searchEndpoint: operationId => endpoints.payoutOperations(operationId)
  });
  const handleGenerateReport = () => {
    if (activeFilters) {
      const { from, to, operation_id, status } = activeFilters;
      const reportFilters: PayoutReportCreateForm = {
        name: '',
        from,
        to: handleEndDateFilter(to),
        operationIds: operation_id,
        statuses: status
      };
      history.push(PORTAL_PATH.PAYOUT_REPORTS, { filters: reportFilters });
    }
  };

  const tableRowActions: PayoutBillingTableRowActions = {
    openDetails: handleOpenDetailsDialog
  };

  return (
    <>
      <GlobalLoaderOverlay loading={waitingForDialogOpening} />
      <WithFetch
        fetchProvider={fetchWithTokenRefresh}
        pathname={endpoints.payoutBilling}
        options={initFetchOptions}
        withLoader={false}
        withError={false}
      >
        {({ data, refresh, loading, error, previousOptions }: FetchData<PayoutBillingResponse | undefined>) => (
          <WithTableLayout>
            <Table
              dataGridProps={{
                rows: !data?.items || loading ? [] : mapApiDataIntoTableFormat(data.items, tableRowActions),
                columns,
                loading,
                error: {
                  message: error,
                  refresh: () => refresh(previousOptions)
                },
                emptyMsg: billingTableEmptyMsg({
                  view: 'Payout',
                  operationType: 'operations',
                  from: activeFilters.from,
                  to: handleEndDateFilter(activeFilters.to),
                  hasActiveFilters: activeFiltersCountMapper(activeFilters) > 0
                })
              }}
              footerProps={{
                tableInfo: (
                  <TableFooterInfo
                    label={locales.footer.label}
                    tooltip={locales.footer.tooltip}
                    value={data ? formatToAmount(data.totalCost.value) : '-'}
                  />
                ),
                reportProps: {
                  disabled: loading,
                  name: TABLE_ACTION.REPORT,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleGenerateReport
                },
                searchProps: {
                  disabled: loading,
                  name: TABLE_ACTION.SEARCH,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleSearchOperationOpenDialog
                },
                refreshProps: {
                  disabled: loading,
                  handler: () => {
                    refresh(handleRefreshOptions(previousOptions, activeFilters.to));
                  }
                },
                filtersProps: {
                  disabled: loading,
                  filters: activeFilters,
                  activeFiltersCount: activeFiltersCountMapper(activeFilters),
                  form: FilterForm,
                  handleSubmit: (updatedFilters: PayoutBillingFilters) => {
                    const queryParams = getQueryParams({ filters: updatedFilters, apiOptions });

                    handleUpdateFilters({
                      filtersToUpdatePath: FILTERS_PATH.PAYOUT_BILLING_PATH,
                      newFilters: updatedFilters
                    });
                    refresh(queryParams);
                    setActiveFilters(updatedFilters);
                  }
                },
                paginationProps: {
                  prev: {
                    disabled: loading || !data?.pagingContext.prevPageMarker,
                    handler: () => {
                      if (data?.pagingContext.prevPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { prev_marker: data.pagingContext.prevPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  },
                  next: {
                    disabled: loading || !data?.pagingContext.nextPageMarker,
                    handler: () => {
                      if (data?.pagingContext.nextPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { next_marker: data.pagingContext.nextPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  }
                }
              }}
            />
          </WithTableLayout>
        )}
      </WithFetch>
      {selectedOperationDetails && (
        <Dialog handleClose={handleCloseDetailsDialog} open={openDetailsDialog} title={locales.dialog.details.title}>
          <OperationDetails operation={selectedOperationDetails} />
        </Dialog>
      )}
      <Dialog
        open={openSearchOperationDialog}
        title={locales.dialog.search.title}
        handleClose={handleSearchOperationCloseDialog}
      >
        <SearchByOperationId
          state={searchByOperationIdState}
          Results={OperationDetails}
          handleSearch={handleSearchByOperationId}
          isSearching={isSearching}
        />
      </Dialog>
    </>
  );
};

export default Billing;
